import React, { useState, useEffect } from 'react';
import { FaRandom, FaMagic, FaLightbulb, FaChevronDown, FaChevronUp, FaThumbsUp, FaTags, FaHistory } from 'react-icons/fa';
import { ImageModel, PromptPreset, PromptHistory } from '../types';

interface SidebarProps {
  models: ImageModel[];
  selectedModel: ImageModel | null;
  setSelectedModel: (model: ImageModel) => void;
  prompt: string;
  setPrompt: (prompt: string) => void;
  negativePrompt: string;
  setNegativePrompt: (prompt: string) => void;
  aspectRatio: string;
  setAspectRatio: (ratio: string) => void;
  quality: string;
  setQuality: (quality: string) => void;
  seed: string;
  setSeed: (seed: string) => void;
  count: number;
  setCount: (count: number) => void;
  onGenerate: () => void;
  isGenerating: boolean;
  presets: PromptPreset[];
  usePreset: (preset: PromptPreset) => void;
  generateRandomPrompt: () => void;
  promptHistory: PromptHistory[]; 
  useHistoryPrompt: (historyItem: PromptHistory) => void; 
}

const Sidebar: React.FC<SidebarProps> = ({
  models,
  selectedModel,
  setSelectedModel,
  prompt,
  setPrompt,
  negativePrompt,
  setNegativePrompt,
  aspectRatio,
  setAspectRatio,
  quality,
  setQuality,
  seed,
  setSeed,
  count,
  setCount,
  onGenerate,
  isGenerating,
  presets,
  usePreset,
  generateRandomPrompt,
  promptHistory,
  useHistoryPrompt
}) => {
  // 控制各部分的展开/收合状态
  const [isNegativePromptExpanded, setIsNegativePromptExpanded] = useState<boolean>(false);
  const [isSeedExpanded, setIsSeedExpanded] = useState<boolean>(false);
  const [isPromptSuggestionsExpanded, setIsPromptSuggestionsExpanded] = useState<boolean>(false);
  const [isTagsExpanded, setIsTagsExpanded] = useState<boolean>(false);
  const [isHistoryExpanded, setIsHistoryExpanded] = useState<boolean>(false);
  const [isCombinerExpanded, setIsCombinerExpanded] = useState<boolean>(false); // 添加提示词组合器展开状态
  
  // 提示词组合器状态
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [combinedPrompt, setCombinedPrompt] = useState<string>('');

  // 根据选择的模型类别过滤提示词
  const [filteredPresets, setFilteredPresets] = useState<PromptPreset[]>([]);
  
  // 常用提示词标签
  const commonTags: Record<string, Array<{text: string, className: string}>> = {
    '画质增强': [
      { text: '高清细节', className: 'tag-primary' },
      { text: '4K超高清', className: 'tag-primary' },
      { text: '精细纹理', className: 'tag-primary' },
      { text: '锐利图像', className: 'tag-primary' }
    ],
    '光照效果': [
      { text: '柔和光线', className: 'tag-secondary' },
      { text: '逆光', className: 'tag-secondary' },
      { text: '黄金时段', className: 'tag-secondary' },
      { text: '体积光', className: 'tag-secondary' }
    ],
    '构图技巧': [
      { text: '黄金比例', className: 'tag-success' },
      { text: '对称构图', className: 'tag-success' },
      { text: '景深效果', className: 'tag-success' },
      { text: '广角视角', className: 'tag-success' }
    ],
    '艺术风格': [
      { text: '电影感', className: 'tag-warning' },
      { text: '梦幻氛围', className: 'tag-warning' },
      { text: '未来主义', className: 'tag-warning' },
      { text: '极简主义', className: 'tag-warning' }
    ]
  };
  
  // 添加标签到提示词
  const addTagToPrompt = (tag: string) => {
    // 如果提示词为空或者最后一个字符不是逗号或空格，则添加逗号和空格
    const separator = prompt && !prompt.endsWith(', ') && !prompt.endsWith(' ') ? ', ' : '';
    setPrompt(prompt + separator + tag);
  };
  
  // 添加或移除标签到组合器
  const toggleTagInCombiner = (tag: string) => {
    if (selectedTags.includes(tag)) {
      setSelectedTags(prev => prev.filter(t => t !== tag));
    } else {
      setSelectedTags(prev => [...prev, tag]);
    }
  };
  
  // 生成组合提示词
  const generateCombinedPrompt = () => {
    if (selectedTags.length === 0) return;
    
    const combined = selectedTags.join(', ');
    setCombinedPrompt(combined);
  };
  
  // 应用组合提示词
  const applyCombinedPrompt = () => {
    if (!combinedPrompt) return;
    
    if (prompt) {
      // 如果已有提示词，添加到末尾
      const separator = prompt.endsWith(', ') || prompt.endsWith(' ') ? '' : ', ';
      setPrompt(prompt + separator + combinedPrompt);
    } else {
      // 如果没有提示词，直接设置
      setPrompt(combinedPrompt);
    }
    
    // 清空组合器
    setSelectedTags([]);
    setCombinedPrompt('');
  };
  
  // 当选择的模型改变时，更新过滤后的提示词
  useEffect(() => {
    if (selectedModel && selectedModel.category && presets.length > 0) {
      const categoryPresets = presets.filter(preset => 
        preset.category === selectedModel.category
      );
      
      // 如果找到匹配的提示词，则使用它们，否则使用所有提示词
      setFilteredPresets(categoryPresets.length > 0 ? categoryPresets : presets);
      
      // 不再自动展开提示词建议，让用户手动点击展开
      // setIsPromptSuggestionsExpanded(true);
    } else {
      setFilteredPresets(presets);
      // 如果没有选择模型，保持提示词建议收合
      // setIsPromptSuggestionsExpanded(false);
    }
  }, [selectedModel, presets]);
  
  // 使用提示词预设
  const handleUsePreset = (preset: PromptPreset) => {
    usePreset(preset);
    // 使用后关闭提示词建议
    setIsPromptSuggestionsExpanded(false);
  };
  
  // 格式化日期显示
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // 截断长文本
  const truncateText = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  return (
    <aside className="w-full md:w-64 lg:w-72 bg-dark-800 border-r border-dark-700 p-2 overflow-y-auto">
      <div className="space-y-2">
        {/* 模型选择 - 下拉选择框 */}
        <div className="space-y-3">
          <h2 className="text-xl font-semibold text-white">选择AI模型</h2>
          <div className="relative">
            <select
              className="input w-full py-3 appearance-none bg-dark-700 cursor-pointer pr-10"
              value={selectedModel?.id || ''}
              onChange={(e) => {
                const selectedId = e.target.value;
                const model = models.find(m => m.id === selectedId);
                if (model) setSelectedModel(model);
              }}
            >
              <option value="" disabled>请选择AI模型</option>
              
              {/* 按类别分组显示模型 */}
              <optgroup label="核心基础模型">
                {models.filter(model => model.category === '核心基础模型').map((model) => (
                  <option key={model.id} value={model.id}>
                    {model.name}
                  </option>
                ))}
              </optgroup>
              
              <optgroup label="动漫与二次元专用">
                {models.filter(model => model.category === '动漫与二次元专用').map((model) => (
                  <option key={model.id} value={model.id}>
                    {model.name}
                  </option>
                ))}
              </optgroup>
              
              <optgroup label="欧美卡通与3D风格">
                {models.filter(model => model.category === '欧美卡通与3D风格').map((model) => (
                  <option key={model.id} value={model.id}>
                    {model.name}
                  </option>
                ))}
              </optgroup>
              
              <optgroup label="艺术与特殊效果">
                {models.filter(model => model.category === '艺术与特殊效果').map((model) => (
                  <option key={model.id} value={model.id}>
                    {model.name}
                  </option>
                ))}
              </optgroup>
              
              {/* 兼容旧版本 - 显示没有类别的模型 */}
              {models.filter(model => !model.category).length > 0 && (
                <optgroup label="其他模型">
                  {models.filter(model => !model.category).map((model) => (
                    <option key={model.id} value={model.id}>
                      {model.name}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
              <FaChevronDown className="text-dark-300" />
            </div>
          </div>
          
          {/* 显示选中模型的详细信息 */}
          {selectedModel && (
            <div className="mt-2 p-3 rounded-lg border border-dark-600 bg-dark-700">
              <h3 className="font-medium text-white">{selectedModel.name}</h3>
              {selectedModel.category && (
                <span className="inline-block px-2 py-1 mt-1 text-xs rounded-full bg-primary-600 text-white">
                  {selectedModel.category}
                </span>
              )}
              <p className="text-sm text-dark-300 mt-2">{selectedModel.description}</p>
              <div className="flex flex-wrap gap-1 mt-2">
                {selectedModel.capabilities && selectedModel.capabilities.map((capability, index) => (
                  <span
                    key={index}
                    className="px-2 py-1 text-xs rounded-full bg-dark-600 text-dark-200"
                  >
                    {capability}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* 提示词输入 */}
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-white">提示词</h2>
            <div className="flex space-x-2">
              <button
                onClick={generateRandomPrompt}
                className="p-2 rounded-lg bg-dark-700 hover:bg-dark-600 text-dark-200 transition-all duration-200"
                title="随机提示词"
              >
                <FaRandom />
              </button>
              <button
                onClick={() => setPrompt('')}
                className="p-2 rounded-lg bg-dark-700 hover:bg-dark-600 text-dark-200 transition-all duration-200"
                title="清空提示词"
              >
                清空
              </button>
            </div>
          </div>
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="描述你想要创建的图像..."
            className="input w-full h-32 resize-none"
          />

          {/* 随机提示词功能已在上方按钮中实现 */}
        </div>

        {/* 负面提示词 - 可展开/收合 */}
        <div className="space-y-3">
          <div className="flex justify-between items-center cursor-pointer" 
               onClick={() => setIsNegativePromptExpanded(!isNegativePromptExpanded)}>
            <h2 className="text-xl font-semibold text-white">负面提示词</h2>
            {isNegativePromptExpanded ? 
              <FaChevronUp className="text-dark-300" /> : 
              <FaChevronDown className="text-dark-300" />}
          </div>
          
          {isNegativePromptExpanded && (
            <textarea
              value={negativePrompt}
              onChange={(e) => setNegativePrompt(e.target.value)}
              placeholder="描述你不想在图像中出现的元素..."
              className="input w-full h-20 resize-none"
            />
          )}
        </div>

        {/* 提示词建议 */}
        <div className="space-y-3">
          <div className="flex justify-between items-center cursor-pointer" 
               onClick={() => setIsPromptSuggestionsExpanded(!isPromptSuggestionsExpanded)}>
            <h2 className="text-xl font-semibold text-white">提示词建议</h2>
            {isPromptSuggestionsExpanded ? 
              <FaChevronUp className="text-dark-300" /> : 
              <FaChevronDown className="text-dark-300" />}
          </div>
          
          {isPromptSuggestionsExpanded && (
            <div className="space-y-3">
              {/* 模型专属提示词 */}
              {selectedModel && selectedModel.category && (
                <div className="mb-4">
                  <div className="text-sm font-medium text-primary-500 mb-2">
                    {selectedModel.category}专属提示词
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {filteredPresets
                      .filter(preset => preset.category === selectedModel.category)
                      .map((preset, index) => (
                        <button
                          key={`${selectedModel.category}-${index}`}
                          onClick={() => handleUsePreset(preset)}
                          className="p-2 text-sm text-left rounded-lg bg-dark-700 hover:bg-dark-600 text-dark-200 hover:text-white transition-all duration-200 border border-dark-600"
                          title={preset.prompt}
                        >
                          {preset.name}
                        </button>
                      ))}
                  </div>
                </div>
              )}
              
              {/* 移除"所有提示词"部分，只显示分类提示词 */}
              {selectedModel && (
                <div>
                  {/* 按类别分组显示提示词 */}
                  {Array.from(new Set(presets.filter(p => p.category).map(p => p.category))).map((category) => {
                    // 跳过当前选中模型的类别，因为已经在上面显示了
                    if (selectedModel.category === category) return null;
                    
                    const categoryPresets = presets.filter(p => p.category === category);
                    if (categoryPresets.length === 0) return null;
                    
                    return (
                      <React.Fragment key={category}>
                        <div className="text-sm font-medium text-dark-300 mb-2 mt-3">
                          {category}提示词
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          {categoryPresets.map((preset, index) => (
                            <button
                              key={`${category}-${index}`}
                              onClick={() => handleUsePreset(preset)}
                              className="p-2 text-sm text-left rounded-lg bg-dark-700 hover:bg-dark-600 text-dark-200 hover:text-white transition-all duration-200 border border-dark-600"
                              title={preset.prompt}
                            >
                              {preset.name}
                            </button>
                          ))}
                        </div>
                      </React.Fragment>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* 提示词标签 */}
        <div className="space-y-3">
          <div className="flex justify-between items-center cursor-pointer" 
               onClick={() => setIsTagsExpanded(!isTagsExpanded)}>
            <h2 className="text-xl font-semibold text-white">提示词标签</h2>
            {isTagsExpanded ? 
              <FaChevronUp className="text-dark-300" /> : 
              <FaChevronDown className="text-dark-300" />}
          </div>
          
          {isTagsExpanded && (
            <div className="space-y-3">
              {Object.keys(commonTags).map((category, index) => (
                <div key={index} className="mb-2">
                  <div className="text-sm font-medium text-dark-300 mb-2">{category}</div>
                  <div className="grid grid-cols-2 gap-2">
                    {commonTags[category].map((tag, tagIndex) => (
                      <button
                        key={tagIndex}
                        onClick={() => addTagToPrompt(tag.text)}
                        className={`p-2 text-sm text-left rounded-lg tag-suggestion ${tag.className} text-dark-200 hover:text-white transition-all duration-200 border border-dark-600`}
                      >
                        {tag.text}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 提示词组合器 */}
        <div className="space-y-3">
          <div className="flex justify-between items-center cursor-pointer" 
               onClick={() => setIsCombinerExpanded(!isCombinerExpanded)}>
            <h2 className="text-xl font-semibold text-white">提示词组合器</h2>
            {isCombinerExpanded ? 
              <FaChevronUp className="text-dark-300" /> : 
              <FaChevronDown className="text-dark-300" />}
          </div>
          
          {isCombinerExpanded && (
            <div className="space-y-3">
              {Object.keys(commonTags).map((category, index) => (
                <div key={index} className="mb-2">
                  <div className="text-sm font-medium text-dark-300 mb-2">{category}</div>
                  <div className="grid grid-cols-2 gap-2">
                    {commonTags[category].map((tag, tagIndex) => (
                      <button
                        key={tagIndex}
                        onClick={() => toggleTagInCombiner(tag.text)}
                        className={`p-2 text-sm text-left rounded-lg tag-suggestion ${tag.className} text-dark-200 hover:text-white transition-all duration-200 border border-dark-600 ${selectedTags.includes(tag.text) ? 'bg-primary-600 text-white' : ''}`}
                      >
                        {tag.text}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
              <button
                onClick={generateCombinedPrompt}
                className="p-2 text-sm text-left rounded-lg bg-dark-700 hover:bg-dark-600 text-dark-200 hover:text-white transition-all duration-200 border border-dark-600 w-full"
              >
                生成组合提示词
              </button>
              <button
                onClick={applyCombinedPrompt}
                className="p-2 text-sm text-left rounded-lg bg-dark-700 hover:bg-dark-600 text-dark-200 hover:text-white transition-all duration-200 border border-dark-600 w-full"
              >
                应用组合提示词
              </button>
              <p className="text-sm text-dark-300">{combinedPrompt}</p>
            </div>
          )}
        </div>

        {/* 提示词历史记录 */}
        <div className="space-y-3">
          <div className="flex justify-between items-center cursor-pointer" 
               onClick={() => setIsHistoryExpanded(!isHistoryExpanded)}>
            <h2 className="text-xl font-semibold text-white">提示词历史记录</h2>
            {isHistoryExpanded ? 
              <FaChevronUp className="text-dark-300" /> : 
              <FaChevronDown className="text-dark-300" />}
          </div>
          
          {isHistoryExpanded && (
            <div className="space-y-3">
              {promptHistory.map((historyItem, index) => (
                <div key={index} className="p-3 rounded-lg border border-dark-600 bg-dark-700">
                  <div className="flex justify-between items-center mb-2">
                    <h3 className="font-medium text-white">{truncateText(historyItem.prompt, 20)}</h3>
                    <span className="text-xs text-dark-400">{formatDate(historyItem.created_at)}</span>
                  </div>
                  <button
                    onClick={() => useHistoryPrompt(historyItem)}
                    className="p-2 text-sm text-left rounded-lg bg-dark-700 hover:bg-dark-600 text-dark-200 hover:text-white transition-all duration-200 border border-dark-600 w-full"
                  >
                    使用此提示词
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 图像设置 */}
        <div className="space-y-3">
          <h2 className="text-xl font-semibold text-white">图像设置</h2>
          
          {/* 宽高比 */}
          <div>
            <label className="block text-sm font-medium text-dark-200 mb-1">
              宽高比
            </label>
            <div className="grid grid-cols-5 gap-2">
              {['1:1', '16:9', '4:3', '3:4', '9:16'].map((ratio) => (
                <button
                  key={ratio}
                  onClick={() => setAspectRatio(ratio)}
                  className={`py-2 text-center rounded ${
                    aspectRatio === ratio
                      ? 'bg-primary-600 text-white'
                      : 'bg-dark-700 text-dark-300 hover:bg-dark-600'
                  }`}
                >
                  {ratio}
                </button>
              ))}
            </div>
          </div>
          
          {/* 种子值 */}
          <div className="space-y-2">
            <div className="flex justify-between items-center cursor-pointer" 
                 onClick={() => setIsSeedExpanded(!isSeedExpanded)}>
              <label className="block text-sm font-medium text-dark-200">
                种子值 {seed && <span className="text-xs text-primary-400">({seed})</span>}
              </label>
              {isSeedExpanded ? 
                <FaChevronUp className="text-dark-300" /> : 
                <FaChevronDown className="text-dark-300" />}
            </div>
            
            {isSeedExpanded && (
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={seed}
                    onChange={(e) => {
                      // 只允许输入数字
                      const value = e.target.value;
                      if (value === '' || /^[0-9]+$/.test(value)) {
                        setSeed(value);
                      }
                    }}
                    placeholder="输入种子值（可选）"
                    className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white placeholder-dark-400 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  />
                  <button
                    onClick={() => setSeed('')}
                    className="px-2 py-1 bg-dark-700 text-dark-300 hover:text-white rounded"
                    title="清除种子值"
                  >
                    ✕
                  </button>
                </div>
                <p className="text-xs text-dark-400">使用相同的种子值可以生成相同的图片</p>
              </div>
            )}
          </div>
          
          {/* 生成数量 */}
          <div>
            <label className="block text-sm font-medium text-dark-200 mb-1">
              生成数量
            </label>
            <div className="grid grid-cols-4 gap-2">
              {[1, 2, 3, 4].map((num) => (
                <button
                  key={num}
                  onClick={() => setCount(num)}
                  className={`py-2 text-center rounded ${
                    count === num
                      ? 'bg-primary-600 text-white'
                      : 'bg-dark-700 text-dark-300 hover:bg-dark-600'
                  }`}
                >
                  {num}张
                </button>
              ))}
            </div>
          </div>
          
          {/* 画质 - 隐藏画质选择，自动使用超清 */}
          <div className="hidden">
            <input type="hidden" value="ultra" />
          </div>
        </div>

        {/* 生成按钮 */}
        <button
          onClick={onGenerate}
          disabled={isGenerating || !prompt.trim() || !selectedModel}
          className={`btn w-full py-3 flex items-center justify-center space-x-2 ${
            isGenerating || !prompt.trim() || !selectedModel
              ? 'bg-dark-600 text-dark-400 cursor-not-allowed'
              : 'bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-700 hover:to-secondary-700 text-white'
          }`}
        >
          <FaMagic className={isGenerating ? 'animate-spin' : ''} />
          <span>{isGenerating ? '生成中...' : '生成图像'}</span>
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
